import React, { ComponentType } from "react"
import { NodeProps } from "reactflow"

import { BlockNodeData } from "@/protocols/chatBotFlow"

import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"
import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySectionGroup from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionGroup"
import { Grid } from "@material-ui/core"
import {
	Clear as ClearIcon
} from "@material-ui/icons"

import { getCurrentApiIntegrationUrlAndMethod, getCurrentCustomFieldWebhookAssociationData, getCurrentHeaderData } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"
import { getConditionRuleByValidationSlug } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionHandle from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionHandle"
import BlockBodySectionRequiredCondition, { ContinueConditionIcon } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionRequiredCondition"
import APIBlockHeaderSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/APIBlock/APIBlockHeaderSectionEditor"
import APIBlockCustomFieldSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/APIBlock/APIBlockCustomFieldSectionEditor"
import APIBlockBaseRequestDataSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/APIBlock/APIBlockBaseRequestDataSectionEditor"
import ContrastChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ContrastChip"
import { colors } from "@/styles/colors"

const APIBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })
	const processedRequestConditionRule = getConditionRuleByValidationSlug("api-request-with-success-response", chatBotFlowBlockDetails.nextChatBotFlowBlockRules)
	const requestWithErrorConditionRule = getConditionRuleByValidationSlug("api-response-with-error", chatBotFlowBlockDetails.nextChatBotFlowBlockRules)
	const unprocessedRequestConditionRule = getConditionRuleByValidationSlug("api-request-without-response", chatBotFlowBlockDetails.nextChatBotFlowBlockRules)

	const getUrlData = () => {
		const urlData = getCurrentApiIntegrationUrlAndMethod(chatBotFlowBlockDetails.content)

		if (urlData.method && urlData.url) {
			return (
				<Grid container spacing={1}>
					<Grid item>
						<ContrastChip
							backgroundColor={colors.gray[100]}
							title={String(urlData?.method) || "Método não configurado"}
						/>
					</Grid>
					<Grid item>
						<ContrastChip
							backgroundColor={colors.gray[100]}
							style={{
								maxWidth: "26ch"
							}}
							title={String(urlData?.url) || "Url não configurada"}
						/>
					</Grid>
				</Grid>
			)
		} else {
			return null
		}
	}

	const getHeaderData = () => {
		const headers = getCurrentHeaderData(chatBotFlowBlockDetails.content)

		if (headers && headers?.length > 0) {
			return (
				<>
					{headers?.map((header, index) => {
						return (
							<Grid container direction="row" wrap="nowrap" key={index} spacing={1}>
								<Grid item>
									<ContrastChip
										backgroundColor={colors.gray[100]}
										style={{
											maxWidth: "16ch",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap"
										}}
										title={header?.key || "Chave não mapeado"}
									/>
								</Grid>
								<Grid item xs zeroMinWidth>
									<ContrastChip
										backgroundColor={colors.gray[100]}
										style={{
											maxWidth: "15ch",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap"
										}}
										title={header?.value || "Valor não mapeado"}
									/>
								</Grid>
							</Grid>
						)
					})}
				</>
			)
		} else {
			return <Grid item>
				<ContrastChip
					backgroundColor={colors.gray[100]}
					style={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
					title={"Sem cabeçalhos configurados"}
				/>
			</Grid>
		}
	}

	const getCustomFieldData = () => {
		const customFieldAssociations = getCurrentCustomFieldWebhookAssociationData(chatBotFlowBlockDetails.content)

		if (customFieldAssociations?.length > 0) {
			return (
				<>
					{customFieldAssociations.map((customField, index) => {
						return (<Grid container direction="row" wrap="nowrap" key={index} spacing={1}>
							<Grid item>
								<ContrastChip
									backgroundColor={colors.gray[100]}
									style={{
										maxWidth: "16ch",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
									title={customField?.customFieldName || "Campo não mapeado"}
								/>
							</Grid>
							<Grid item xs zeroMinWidth>
								<ContrastChip
									backgroundColor={colors.blue[100]}
									style={{
										maxWidth: "15ch",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
									title={customField?.webhookField || "Campo não mapeado"}
								/>
							</Grid>
						</Grid>)
					})}
				</>
			)
		} else {
			return <Grid item>
				<ContrastChip
					backgroundColor={colors.gray[100]}
					style={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
					title={"Nenhum campo personalizado mapeado"}
				/>
			</Grid>
		}
	}

	return (
		<BaseBlock
			node={node}
		>
			<BlockBodySectionGroup>
				<BlockBodySection
					fullWidth
				>
					<APIBlockBaseRequestDataSectionEditor
						onSave={content => chatBotFlowBlockDetails.changeContent(content)}
						chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					>
						<BlockBodySectionContainer
							text={getUrlData()}
							placeholder="Requisição não configurada"
							title="Configurações da requisição"
							chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
							errorType="api_block_missing_url"
						/>
					</APIBlockBaseRequestDataSectionEditor>
				</BlockBodySection>
			</BlockBodySectionGroup>

			<BlockBodySectionGroup>
				<BlockBodySection
					fullWidth
				>
					<APIBlockHeaderSectionEditor
						onSave={content => chatBotFlowBlockDetails.changeContent(content)}
						chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					>
						<BlockBodySectionContainer
							text={getHeaderData()}
							placeholder="Nenhum cabeçalho configurado"
							title="Cabeçalhos"
							chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
						/>
					</APIBlockHeaderSectionEditor>
				</BlockBodySection>
			</BlockBodySectionGroup>

			<BlockBodySectionGroup>
				<BlockBodySection
					fullWidth
				>
					<APIBlockCustomFieldSectionEditor
						onSave={content => chatBotFlowBlockDetails.changeContent(content)}
						chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					>
						<BlockBodySectionContainer
							text={getCustomFieldData()}
							placeholder="Nenhum campo mapeado"
							title="Salvar Resposta"
							chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
							errorType="api_block_custom_field"
						/>
					</APIBlockCustomFieldSectionEditor>
				</BlockBodySection>
			</BlockBodySectionGroup>

			<BlockBodySectionGroup>
				{processedRequestConditionRule && (
					<BlockBodySection>
						<BlockBodySectionHandle
							chatBotFlowBlockRule={processedRequestConditionRule}
						>
							<BlockBodySectionRequiredCondition
								text="Se a resposta for sucesso"
								icon={<ContinueConditionIcon />}
							/>
						</BlockBodySectionHandle>
					</BlockBodySection>
				)}

				{requestWithErrorConditionRule && (
					<BlockBodySection>
						<BlockBodySectionHandle
							chatBotFlowBlockRule={requestWithErrorConditionRule}
						>
							<BlockBodySectionRequiredCondition
								text="Próximo passo se a resposta for erro"
								icon={<ClearIcon />}
							/>
						</BlockBodySectionHandle>
					</BlockBodySection>
				)}

				{unprocessedRequestConditionRule && (
					<BlockBodySection>
						<BlockBodySectionHandle
							chatBotFlowBlockRule={unprocessedRequestConditionRule}
						>
							<BlockBodySectionRequiredCondition
								text="Próximo passo se não houver resposta"
								icon={<ClearIcon />}
							/>
						</BlockBodySectionHandle>
					</BlockBodySection>
				)}
			</BlockBodySectionGroup>
		</BaseBlock>
	)
}

export default APIBlock
